import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import Card from "../../atoms/Card";
import StatCard from "../../atoms/StatCard";
import { formatCurrency } from "../../../utils/formatCurrency";
import { optionsTypesInmuebles } from "../../../utils/optionsTypesInmuebles";
import { useMemo, useState } from "react";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const StatsDashboard = ({ dataInmuebles, dataGastos }) => {
    const totalInmuebles = dataInmuebles.length || 0;
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    const inmuebleOptions = useMemo(() => {
        return dataInmuebles.map(inm => ({
            value: `inmobiliarias/${inm.real_estate}/${inm.id}`,
            label: inm.name || `Inmueble ${inm.id}`,
            real_estate: inm.real_estate,
            id: inm.id
        }));
    }, [dataInmuebles]);

    const fuse = useMemo(() => {
        return new Fuse(inmuebleOptions, {
            keys: ["label", "value"],
            threshold: 0.3
        });
    }, [inmuebleOptions]);

    const filteredOptions = query
        ? fuse.search(query).map(res => res.item)
        : inmuebleOptions;

    const handleChange = (option) => {
        setSelected(option);
        if (option) {
            navigate(`/${option.value}`);
        }
    };

    let totalGastos = 0;

    dataGastos.forEach(g => {
        totalGastos= g.amount + totalGastos;
    });
    const rentedCount = dataInmuebles.filter(inm => inm.isRented).length;
    const notRentedCount = dataInmuebles.length - rentedCount;

    const mortgagedCount = dataInmuebles.filter(inm => inm.isMortgaged).length;
    const notMortgagedCount = dataInmuebles.length - mortgagedCount;

    const rentedData = [
        { name: "Rentados", value: rentedCount },
        { name: "No rentados", value: notRentedCount },
    ];

    const mortgagedData = [
        { name: "Hipotecados", value: mortgagedCount },
        { name: "No hipotecados", value: notMortgagedCount },
    ];

    const COLORS = ["#8884d8", "#d0d0d0", "#82ca9d", "#ffc658", "#ff8042", "#a4de6c"];

    const dataInmobiliarias = [
        { id:1, name:"Aledra" },
        { id:2, name:"Comerdesa" },
        { id:3, name:"Otros" },
    ];

    const inmobiliariaCountMap = {};
    dataInmuebles.forEach(inm => {
        const id = inm.real_estate || "3";
        inmobiliariaCountMap[id] = (inmobiliariaCountMap[id] || 0) + 1;
    });

    const inmobiliariaData = Object.entries(inmobiliariaCountMap).map(([id, count]) => {
        const name = dataInmobiliarias.find(inm => Number(inm.id) === Number(id))?.name || "Desconocido";
        return { name, value: count };
    });

    const typeCountMap = {};
    dataInmuebles.forEach(inm => {
        const type = inm.type || "Desconocido";
        typeCountMap[type] = (typeCountMap[type] || 0) + 1;
    });

    const tiposData = optionsTypesInmuebles.map(opt => ({
        name: opt.label,
        value: typeCountMap[opt.value] || 0
    }));

    return (
        <Card>
            <div>
                <Select
                    placeholder="Ir a inmueble..."
                    options={filteredOptions}
                    value={selected}
                    onInputChange={(val) => setQuery(val)}
                    onChange={handleChange}
                    className="border-2 border-slate-300 rounded-md"
                />
            </div>
            <div className="w-full py-4 grid grid-cols-2 gap-4">
                <StatCard title="Total de inmuebles" content={totalInmuebles} />
                <StatCard title="Total de gastos" content={formatCurrency(totalGastos)} />
            </div>
            <div className="w-full grid md:grid-cols-2 gap-6 ">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-center">Estado de Renta</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={rentedData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                            >
                                {rentedData.map((entry, index) => (
                                    <Cell key={`cell-rent-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-center">Estado de Hipoteca</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={mortgagedData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                            >
                                {mortgagedData.map((entry, index) => (
                                    <Cell key={`cell-mort-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold mb-2 text-center">Inmuebles por Tipo</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={tiposData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="value" fill="#8884d8" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold mb-2 text-center">Inmuebles por Inmobiliaria</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={inmobiliariaData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label
                            >
                                {inmobiliariaData.map((entry, index) => (
                                    <Cell key={`cell-inmo-${index+2}`} fill={COLORS[index+2 % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Card>
    );
};

export default StatsDashboard;
